import PanelError from '@wandb/weave/common/components/elements/PanelError';
import {GifViewer} from '@wandb/weave/components/Panel2/GifViewer';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {TruncateByCharsWithTooltip} from '@wandb/weave/components/TruncateByCharsWithTooltip';
import _ from 'lodash';
import * as React from 'react';
import {useState} from 'react';

import {labelComponent, makeCaptions} from '../util/media';
import {InstrumentedCenteredWaveLoader as Loader} from './../components/utility/InstrumentedLoader';
import {type MediaCardProps} from './MediaCard';
import MessageMediaNotFound from './MessageMediaNotFound';
import {SingleRunLink} from './MultiRunWorkspace/SingleRunMode';
import {useGetTrackingCallbacks} from './PanelMediaBrowser/MediaBrowserPanelTileTrackingContext';
import {getModeConfig} from './PanelMediaBrowser/modes/getModeConfig';
import {useVideoSync} from './PanelMediaBrowser/sync/useVideoSync';
import {StreamingVideoViewer} from './VideoStreaming/StreamingVideoViewer';
import {videoMimeTypeFromFilename} from './VideoStreaming/videoMimeTypeFromFilename';

/** Keep the in-tile summary short; full text lives in the Weave tooltip. */
const PLAYBACK_ERROR_VISIBLE_CHARS = 72;

type VideoCardProps = MediaCardProps;

/**
 * Stock PanelError (icon + default message formatting). Truncates the message
 * string when needed and shows the full text in Weave's tooltip on hover.
 */
function VideoPlaybackError({text}: {text: string}) {
  return (
    <div data-testid="video-playback-error">
      <TruncateByCharsWithTooltip
        text={text}
        maxChars={PLAYBACK_ERROR_VISIBLE_CHARS}
        Wrapper={null}>
        {({truncatedText}) => (
          <PanelError
            message={truncatedText}
            logErrorMessage={`Video playback error: ${text}`}
          />
        )}
      </TruncateByCharsWithTooltip>
    </div>
  );
}

// a single video card with optional synchronization support
const VideoCard = React.memo((props: VideoCardProps) => {
  const {
    run,
    mediaKey,
    mediaIndex,
    runSignature,
    globalStep,
    tileMedia,
    loading,
    customRunName,
    tileMediaSpec,
    getMediaTracking,
    config,
  } = props;
  // While we are waiting for the blob to be downloaded, we need to show a loading state
  // or else the download button will download an empty txt file corresponding to the null blob.
  const isBackgroundDownloadingBlob = tileMedia?.isNullBlob ?? false;
  const objectURL = tileMedia?.objectURL ?? '';
  const filename = tileMedia?.filePath
    ? _.last(tileMedia.filePath.split('/'))
    : undefined;

  // Generate unique video ID for synchronization
  const videoId = `${run.name}-${tileMedia?.step ?? ''}-${mediaKey}-${mediaIndex}`;

  const {videoRefCallback, isSyncEnabled} = useVideoSync({
    videoId,
    isVideoSychronized: config?.videoPlaybackSync ?? false,
  });

  const titleLink = (
    <SingleRunLink
      {...runSignature}
      linkText={customRunName ?? run.displayName}
    />
  );

  // get captions for all images in the group
  const captions = props.omitCaptions
    ? []
    : makeCaptions(tileMedia?.historyRow[mediaKey], mediaIndex);

  const loadingElement = <Loader name="panel-content-loader" size="small" />;

  const {trackSuccess, trackError} = useGetTrackingCallbacks(
    tileMedia,
    tileMediaSpec,
    getMediaTracking
  );

  const hlsSrc = tileMedia?.hlsSrc;
  const mimeTypeInfo = videoMimeTypeFromFilename(filename);

  // In a content-sized layout the tile sizes to its own content, so the viewer
  // pins itself to its decoded dimensions rather than filling the tile. The
  // layout decision lives on the mode config.
  const sizeToIntrinsic = getModeConfig(config?.mode).shouldSizeTilesByContent(
    config ?? {},
    'video'
  );

  const sharedProps = {
    headerElement: labelComponent(props, tileMedia?.step, titleLink),
    footerElement:
      captions.length > 0 ? (
        <div className="image-card-caption">{captions}</div>
      ) : undefined,
    trackError,
    width: props.width,
    height: props.height,
    sizeToIntrinsic,
    refCallback: isSyncEnabled ? videoRefCallback : undefined,
    // videos default to autoplay and loop prior to implementing synchronization
    autoPlay: isSyncEnabled ? undefined : true,
    // need to disable autoloop if sync is enabled
    autoLoop: isSyncEnabled ? undefined : true,
  };

  const [hasFailedToLoadStream, setHasFailedToLoadStream] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [playbackError, setPlaybackError] = useState<string | null>(null);
  const onPlaybackFailed = React.useCallback((message: string) => {
    setPlaybackError(message);
  }, []);
  const failedLoadElement = (
    <MessageMediaNotFound
      mediaIndex={mediaIndex}
      mediaKey={mediaKey}
      stepIndex={globalStep}
      mediaType="videos"
    />
  );

  const gifOnLoaded = React.useCallback(() => {
    trackSuccess({
      // Backend gates who gets streaming assets; FE only opts out via panel config.
      isVideoStreamingEnabledForEntity: hlsSrc != null,
      wasVideoStreamed: false,
      wasVideoReencodedForStreaming: false,
      totalVideoDuration: 0,
      originalVideoFileSize: tileMedia?.blob.size ?? 0,
    });
  }, [trackSuccess, hlsSrc, tileMedia]);

  // Trust backend hlsSrc; panel disableVideoStream is the only FE opt-out.
  const shouldUseHls =
    hlsSrc != null &&
    !hasFailedToLoadStream &&
    config.disableVideoStream !== true;

  const streamingVideoViewerOnSuccess = React.useCallback(
    (totalVideoDuration: number, initialChunkSize?: number) => {
      setVideoLoaded(true);
      trackSuccess({
        isVideoStreamingEnabledForEntity: hlsSrc != null,
        wasVideoStreamed: shouldUseHls,
        wasVideoReencodedForStreaming: hlsSrc != null,
        totalVideoDuration,
        originalVideoFileSize: tileMedia?.blob.size ?? 0,
        initialChunkSize,
      });
    },
    [trackSuccess, tileMedia, hlsSrc, shouldUseHls]
  );

  if (loading) {
    return loadingElement;
  }
  if (tileMedia == null) {
    return failedLoadElement;
  }
  if (mimeTypeInfo.type === 'gif') {
    return (
      <GifViewer
        gifSrc={objectURL}
        gifFilename={filename}
        mediaLoaded={!hasFailedToLoadStream}
        onLoaded={gifOnLoaded}
        onError={trackError}
      />
    );
  }

  // No HLS (missing hlsSrc, panel disable, or prior failure) while the full
  // video is still downloading. Mounting Video.js with both sources null
  // yields CODE 4 ("No compatible source").
  const waitingForPlayableSource = isBackgroundDownloadingBlob && !shouldUseHls;

  const containerSizingClasses = sizeToIntrinsic ? 'h-auto' : 'h-full min-h-0';
  const containerClasses = videoLoaded ? containerSizingClasses : 'hidden';

  if (playbackError != null) {
    return (
      <Tailwind
        className={`px-2 flex flex-col overflow-hidden ${sizeToIntrinsic ? 'w-auto h-auto' : 'w-full h-full min-h-0'}`}>
        {sharedProps.headerElement}
        <VideoPlaybackError text={playbackError} />
        {sharedProps.footerElement}
      </Tailwind>
    );
  }

  return (
    <Tailwind
      className={`px-2 flex flex-col overflow-hidden ${sizeToIntrinsic ? 'w-auto h-auto' : 'w-full h-full min-h-0'}`}>
      {(!videoLoaded || waitingForPlayableSource) && loadingElement}
      {!waitingForPlayableSource && (
        <div className={containerClasses}>
          <StreamingVideoViewer
            key={config.videoPlayerTimeStep} // force re-render when video player time step changes to re-initialize the video player with the new seek step
            hlsSrc={shouldUseHls ? hlsSrc : null}
            trackSuccess={streamingVideoViewerOnSuccess}
            {...sharedProps}
            setHasFailedToLoadStream={setHasFailedToLoadStream}
            onPlaybackFailed={onPlaybackFailed}
            downloadUrl={isBackgroundDownloadingBlob ? null : objectURL}
            mimeType={mimeTypeInfo.mimeType}
            videoPlayerTimeStep={config.videoPlayerTimeStep}
          />
        </div>
      )}
    </Tailwind>
  );
});

export default VideoCard;
